import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select, { components } from 'react-select';
import { DateRange } from 'react-date-range';
import { useStaticQuery, graphql } from "gatsby";

// Components //
import Pagination from "../../molecules/pagination/Pagination";
import ThemeCard from '../../molecules/theme-card/themeCard';
import FormRegistroConversacionForo from "../../organisms/form-registro-conversacion-foro/FormRegistroConversacionForo";

import { ReferenciaContenido } from "../../../interfaces/ReferenciaContenido";

// Styles //
import "./forumSection.scss";
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

// Helpers //
import { getStartOfWeek, getStartOfMonth, getStartOfYear, formatDate } from "../../../utils/helpers"

// Data // 
import { Dates, Categories} from "../../../data/foro.data.js"

let PageSize = 5;

interface SelectOptionInterface{
    value: string,
    label: string
}

const ForumSection = (props: ReferenciaContenido) => {
    const data = useStaticQuery(graphql`
        query FORUMSECTIONDATA {
            forumSectionPages: allContentfulSubcategorias(
                filter: { contentful_id: { eq: "4sAl0IvWQMdNDBuY5aLQs3" } }
            ) {
                nodes {
                    referenciaSubcategorias {
                        nombreSubcategoria
                        slug
                        createdAt
                        referenciaInterna {
                            contentful_id
                            referenciaCabecera {
                                migaDePan
                                referenciaMultimedia {
                                    categoriaMultimedia
                                    createdAt
                                    descripcion {
                                        raw
                                    }
                                    imagenPrincipal {
                                        file {
                                            url
                                            contentType
                                            fileName
                                        }
                                    }
                                    nuevoContenido
                                    tematica
                                    textoIntroductorio
                                    tituloMultimedia
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const transformData = (arr) => {
        return arr.map((sub) => { 
            return {
                createdAt: sub.createdAt,
                contentful_id: sub.referenciaInterna.contentful_id,
                slug: sub.slug,
                ...sub.referenciaInterna.referenciaCabecera.referenciaMultimedia,
                // parentContentfulId: sub.referenciaInterna.referenciaContenido[0].contentful_id
            }
        })
    }

    const allData = transformData(data.forumSectionPages.nodes[0].referenciaSubcategorias);
    const [loadedList] = useState(allData.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).sort((a:any, b: any) => a.date - b.date).reverse());
    const [filteredList, setFilteredList] = useState<any[]>(loadedList);
    const [selectedDate, setSelectedDate] = useState<SelectOptionInterface[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<SelectOptionInterface[]>([]);
    const [showDateRange, setShowDateRange] = useState(false);
    const [date, setDate] = useState([{startDate: null, endDate: null, key: 'selection'}]);
    const [currentPage, setCurrentPage] = useState(1);
    const dateRangeRef = useRef(null);

    const currentFilteredList = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * PageSize;
        const lastPageIndex = firstPageIndex + PageSize;
        return filteredList.slice(firstPageIndex, lastPageIndex);
    }, [currentPage, filteredList]);

    const filteredByDate = (option: any) => {
        setSelectedDate(option)
        const sortedDates = loadedList.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).sort((a:any, b: any) => a.date - b.date);

        if (option.value === "Todos") {
            setFilteredList(loadedList);
        }
        
        if (option.value === "Más recientes") {
            let reversedDates = sortedDates.reverse();
            setFilteredList(reversedDates);
            setShowDateRange(false);
        }

        if (option.value === "Más antiguos") {
            setFilteredList(sortedDates);
            setShowDateRange(false);
        }

        if (option.value === "Esta semana") {
            const startWeekDay = getStartOfWeek(new Date());
            const filteredListWeek = loadedList.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= startWeekDay);
            setFilteredList(filteredListWeek);
            setShowDateRange(false);
        }
        
        if (option.value === "Este mes") {
            const startMonthDay = getStartOfMonth(new Date());
            const filteredListMonth = loadedList.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= startMonthDay);
            setFilteredList(filteredListMonth);
            setShowDateRange(false);
        }

        if (option.value === "Este año") {
            const startYearDay = getStartOfYear(new Date());
            const filteredListYear = loadedList.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= startYearDay);
            setFilteredList(filteredListYear);
            setShowDateRange(false);
        }

        if (option.value === "Fecha específica") {
            setShowDateRange(true);
        }
    }
    
    const filteredByCategory = (category: any) => {
        if (category.length > 0) {
            const categoryList = category.map(item => item.value);
            if (categoryList.includes("Todos") && category[0].value === "Todos") {
                setSelectedCategory(Categories);
            } else if (categoryList.includes("Todos") && category[0].value !== "Todos") {
                setSelectedCategory(Categories);
            }
            setFilteredList(loadedList.filter(item => categoryList.includes(item.tematica)))
        }

        if(category.length === 0) {
            setFilteredList(loadedList);
        }
    }

    const filteredBySpecificDate = () => {
        if (date[0].startDate) {
            const filteredListAux = loadedList.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= date[0].startDate && obj.date <= date[0].endDate);
            setFilteredList(filteredListAux);

            if (showDateRange && date[0].endDate !== date[0].startDate) {
                setShowDateRange(false);
                setSelectedDate([{
                    value: formatDate(date[0].startDate).replace(/de/gi, "") + " - " + formatDate(date[0].endDate).replace(/de/gi, ""),
                    label: formatDate(date[0].startDate).replace(/de/gi, "") + " - " + formatDate(date[0].endDate).replace(/de/gi, "")
                }])
            }
        }
    }

    useEffect(() => {
        filteredBySpecificDate();
    }, [date])
    
    useEffect(() => {
        filteredByCategory(selectedCategory);
    },[selectedCategory])
        
    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isSelected ? "#ee2b7b": "#cccccc",
            boxShadow: "#ee2b7b",
            ":hover": {
                borderColor: "#ee2b7b",
            }
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            color: state.isSelected ? "#ee2b7b" : "",
            backgroundColor: state.isSelected  ? "transparent" : "",
            ":hover": {
                color: "#ee2b7b",
            }
        }),
        multiValue: (styles, { data }) => {
            return {
                ...styles,
                backgroundColor: "#ee2b7b",
            };
        },
        multiValueLabel: (styles, { data }) => ({
            ...styles,
            color: "#ffffff",
        }),
        multiValueRemove: (styles, { data }) => ({
            ...styles,
            color: "#ffffff",
            ':hover': {
                backgroundColor: "#ee2b7b",
                color: 'white',
            },
        }),
    }

    const Option = (props) => {
        return (
            <div>
                <components.Option {...props}>
                    <input
                        type="checkbox"
                        checked={props.isSelected}
                        onChange={() => null}
                    />{" "}
                    <label>{props.label}</label>
                </components.Option>
            </div>
        );
    };

    const [displayForm, setDisplayForm] = useState(false);
    const updateDisplayForm = (state: boolean) => { setDisplayForm(state) };

    return (
        <div className="o-forum-section">
            <div className="container">
                <div className="o-forum-section__container">
                    <div className="o-forum-section__container__header">
                        <div className="forum-filters">
                            <div className="forum-filters__theme">
                                <div className="forum-filters__theme__left">
                                    <span><strong>Temas</strong></span>
                                </div>
                                <div className="forum-filters__theme__right">
                                    <div className='btn-filter'>
                                        <button>
                                            Filtrar por:
                                        </button>
                                    </div>
                                    <div className={`forum-filters__theme__right__categories`}>
                                        <div className="forum-filters__theme__right__categories__item">
                                            <p>Fecha de publicación</p>
                                            <Select
                                                onChange={filteredByDate}
                                                // onMenuClose={() => setShowDateRange(false)}
                                                options={Dates}
                                                className="select date-select"
                                                placeholder="Selecciona"
                                                value={selectedDate}
                                                closeMenuOnSelect={false}
                                                styles={styleSelect}
                                            />
                                            {showDateRange && (
                                                <DateRange
                                                    editableDateInputs={true}
                                                    onChange={(item => setDate([item.selection]))}
                                                    moveRangeOnFirstSelection={false}
                                                    ranges={date}
                                                    showSelectionPreview={true}
                                                    color="#FBD4E4"
                                                    rangeColors={["#FBD4E4", "#FFFFFF"]}
                                                    showDateDisplay={false}
                                                    className="date-range"
                                                    ref={dateRangeRef}
                                                />
                                            )}
                                        </div>
                                        <div className="forum-filters__theme__right__categories__item">
                                            <p>Categoría</p>
                                            <Select
                                                onChange={(option: any) => setSelectedCategory(option)}
                                                options={Categories}
                                                className="select category-select"
                                                placeholder="Selecciona"
                                                value={selectedCategory}
                                                isMulti
                                                components={{Option}}
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                styles={styleSelect}
                                            />
                                        </div>
                                        <div className="forum-filters__theme__right__categories__item">
                                            <p>&nbsp;</p>
                                            <button className="forum-filters__theme__right__categories__item_new-theme gtm_boton_nueva_conversacion_foro_comunidades" 
                                                onClick={()=> setDisplayForm(!displayForm)}>
                                                <i className="icon-plus-circle1 gtm_boton_nueva_conversacion_foro_comunidades" />
                                                &nbsp;
                                                <span className="gtm_boton_nueva_conversacion_foro_comunidades">NUEVA CONVERSACION</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="forum-filters__mobile">
                                <button data-menu="filter">Filtrar por:</button>
                                <button data-menu="order">Ordenar por:</button>
                            </div>
                        </div>
                        <div className="forum-new-theme">
                            <button onClick={()=> setDisplayForm(!displayForm)}>
                                <i className="icon-plus-circle1" />
                                &nbsp;
                                <span>NUEVA CONVERSACION</span>
                            </button>
                        </div>
                    </div>
                    <div className="o-forum-section__container__body">
                        <div className="forum-layout">
                            {currentFilteredList && currentFilteredList.map((item, i) => (
                                <ThemeCard {...item}/>
                            ))}
                            {currentFilteredList && currentFilteredList.length === 0 && (
                                <div className="forum-layout__nofound">
                                    <span>¡Vaya!</span>
                                    <p>
                                        No se encontró contenido con los filtros de búsqueda seleccionados.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="o-forum-section__container__footer">
                        <Pagination
                            className="pagination-bar"
                            currentPage={currentPage}
                            totalCount={filteredList.length}
                            pageSize={PageSize}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </div>
                </div>
            </div>

            <FormRegistroConversacionForo displayForm={displayForm} setDisplayForm={updateDisplayForm} />
        </div>
    )
}

export default ForumSection
